import { ColumnMeta, Updater } from '@tanstack/react-table';

export function getAlignmentClass(
  align: ColumnMeta<unknown, unknown>['align'],
) {
  switch (align) {
    case 'right':
      return 'eye:text-right';
    case 'center':
      return 'eye:text-center';
    default:
      return 'eye:text-left';
  }
}

export function getFlexAlignmentClass(
  align: ColumnMeta<unknown, unknown>['align'],
) {
  switch (align) {
    case 'right':
      return 'eye:justify-end';
    case 'center':
      return 'eye:justify-center';
    default:
      return 'eye:justify-start';
  }
}

export function resolveTableUpdater<T>(updater: Updater<T>, old: T): T {
  return typeof updater === 'function'
    ? (updater as (old: T) => T)(old)
    : updater;
}

/**
 * Builds a sliding window of page numbers around the current page.
 * Always returns up to `windowSize` pages with the current page positioned
 * `previousCount` from the start (i.e. 4 before + current + 3 after by default).
 * The window is clamped to [1, total], so near the edges it slides instead
 * of going out of bounds.
 *
 * Examples (total = 20):
 *   current=1   -> [1, 2, 3, 4, 5, 6, 7, 8]
 *   current=5   -> [1, 2, 3, 4, 5, 6, 7, 8]
 *   current=10  -> [6, 7, 8, 9, 10, 11, 12, 13]
 *   current=20  -> [13, 14, 15, 16, 17, 18, 19, 20]
 */
export function getPageRange(
  current: number,
  total: number,
  count = 3,
): number[] {
  if (total <= 0) return [];

  const windowSize = count + 1 + count;

  if (total <= windowSize) {
    return Array.from({ length: total }, (_, i) => i + 1);
  }

  let start = current - count;
  let end = current + count;

  if (start < 1) {
    end += 1 - start;
    start = 1;
  }
  if (end > total) {
    start -= end - total;
    end = total;
  }

  return Array.from({ length: end - start + 1 }, (_, i) => start + i);
}
